import { useIntl } from 'react-intl'
import type { PurchaseInvoice, PurchaseInvoiceFilters } from './types'
import { purchaseInvoiceTableHeaders } from './constants'
import {
	DataTable,
	FontIcon,
	ListItem,
	TableBody,
	TableColumn,
	TableHeader,
	TableRow,
} from 'react-md'
import { TableContainer } from 'ui/StyledComponents/Tables'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { push } from 'connected-react-router'
import PurchaseInvoiceRow from './PurchaseInvoiceRow'
import Pagination from 'ui/components/Pagination'
import { toSnakeCase } from 'util/changeCase'

const StyledDataTable = styled(DataTable)`
	height: calc(100vh - 265px);
	max-height: calc(100vh - 265px);
	td {
		white-space: nowrap;
	}
`
export const TableHeaderNameSortable = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
`

type PurchaseInvoiceTableProps = {
	loading?: boolean
	purchaseInvoices: PurchaseInvoice[]
	hideActions?: boolean
	totalItemCount: number
	itemsPerPage: number
	filters: PurchaseInvoiceFilters
	setFilters: React.Dispatch<React.SetStateAction<PurchaseInvoiceFilters>>
}

const PurchaseInvoiceTable = ({
	loading = false,
	purchaseInvoices,
	totalItemCount,
	itemsPerPage,
	filters,
	setFilters,
	hideActions = false,
}: PurchaseInvoiceTableProps) => {
	const intl = useIntl()
	const dispatch = useDispatch()

	const handlePaginationBack = () => {
		const offset = filters.offset
		if (offset === 0) {
			return
		}
		setFilters((prevData) => ({
			...prevData,
			offset: offset - itemsPerPage,
		}))
	}
	const handlePaginationNext = () => {
		const offset = filters.offset
		const isLastPage = offset + itemsPerPage >= totalItemCount
		if (isLastPage) {
			return
		}
		setFilters((prevData) => ({
			...prevData,
			offset: offset + itemsPerPage,
		}))
	}

	const changeSort = (key: keyof PurchaseInvoice) => {
		setFilters((prevData) => {
			let orderByColumn = toSnakeCase(key)
			let orderByDirection
			if (prevData.orderByColumn === orderByColumn) {
				orderByDirection = prevData.orderByDirection === 'asc' ? 'desc' : 'asc'
			} else {
				orderByDirection = 'desc'
			}

			return {
				...prevData,
				orderByColumn,
				orderByDirection,
			}
		})
	}

	const handleOnClick = (id: number) => {
		dispatch(push(window.location.pathname + '?id=' + id))
	}

	const renderTableHeaders = () => {
		return (
			<TableRow style={{ background: '#fafafa' }}>
				{purchaseInvoiceTableHeaders
					.filter((h) => h.show)
					.map((header) => (
						<TableColumn
							key={header.key}
							onClick={() => header.sortable && changeSort(header.key)}
							style={{ cursor: header.sortable ? 'pointer' : '' }}
						>
							<TableHeaderNameSortable>
								<p className="text-strong text-subtle">{header.title(intl)}</p>
								{filters.orderByColumn === toSnakeCase(header.key) && (
									<FontIcon style={{ fontSize: 12 }}>
										{filters.orderByDirection === 'asc'
											? 'arrow_upward'
											: 'arrow_downward'}
									</FontIcon>
								)}
							</TableHeaderNameSortable>
						</TableColumn>
					))}
				<TableColumn>{/* Empty col for actions */}</TableColumn>
			</TableRow>
		)
	}

	return (
		<TableContainer>
			<StyledDataTable plain>
				<TableHeader
					style={{
						position: 'sticky',
						top: 0,
						zIndex: 1,
					}}
				>
					{renderTableHeaders()}
				</TableHeader>

				<TableBody>
					{purchaseInvoices
						.map((purchaseInvoice, index) => (
							<PurchaseInvoiceRow
								loading={loading}
								hideActions={hideActions}
								key={`${purchaseInvoice.id}-${index}`}
								purchaseInvoice={purchaseInvoice}
								menuItems={[
									<ListItem
										key={1}
										primaryText={intl.formatMessage({
											defaultMessage: 'Poista',
											description: 'Delete',
										})}
										leftIcon={<FontIcon>delete</FontIcon>}
										onClick={() => {}}
										className="md-divider-border md-divider-border--right"
									/>,
								]}
								children={null}
								processing={false}
								handleOnClick={() => handleOnClick(purchaseInvoice.id)}
							/>
						))
						.concat(
							<tr
								key="empty-row"
								className="full-width"
								style={{ height: 100 }}
							></tr>,
						)}
				</TableBody>
				<Pagination
					rowCount={totalItemCount}
					pageIndex={filters.offset / itemsPerPage}
					rowsPerPage={itemsPerPage}
					onBack={handlePaginationBack}
					onNext={handlePaginationNext}
				/>
			</StyledDataTable>
		</TableContainer>
	)
}

export default PurchaseInvoiceTable
